import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Clock, Trophy, ChevronRight, Play, Calendar, Users, Target, CheckCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { supabase } from '@/integrations/supabase/client';
import { format, isToday, isTomorrow, isPast, parseISO } from 'date-fns';
import { bn } from 'date-fns/locale';
import { useActiveLiveMatches } from '@/hooks/useLiveMatch';
import { useLiveEnabled } from '@/hooks/useLiveEnabled';

interface ScheduledMatch {
  id: string;
  match_date: string | null;
  status: string;
  team_a: string | null;
  team_b: string | null;
  score_a: number | null;
  score_b: number | null;
  player_a?: { id: string; name: string; avatar: string | null } | null;
  player_b?: { id: string; name: string; avatar: string | null } | null;
  division?: { name: string } | null;
  tournament?: { name: string } | null;
}

interface LiveMatch {
  id: string;
  match_id: string;
  match_type: string;
  status: string;
  current_period: string;
  score_home: number;
  score_away: number;
  started_at: string;
}

export function LiveMatchSection() {
  const { isEnabled, isLoading: enabledLoading } = useLiveEnabled();
  const [isOpen, setIsOpen] = useState(false);
  const [upcomingMatches, setUpcomingMatches] = useState<ScheduledMatch[]>([]);
  const [recentMatches, setRecentMatches] = useState<ScheduledMatch[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const { liveMatches, isLoading: liveLoading } = useActiveLiveMatches();
  const [pulseScale, setPulseScale] = useState(1);

  // Hide entire section if live system is disabled
  if (enabledLoading || !isEnabled) {
    return null;
  }

  // Pulsing animation for live indicator
  useEffect(() => {
    const interval = setInterval(() => {
      setPulseScale(prev => prev === 1 ? 1.2 : 1);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchData();
    }
  }, [isOpen]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const now = new Date().toISOString();

      // Fetch upcoming matches
      const { data: upcomingData } = await supabase
        .from('matches')
        .select(`
          id, match_date, status, team_a, team_b, score_a, score_b,
          division:divisions(name),
          tournament:tournaments(name)
        `)
        .eq('status', 'scheduled')
        .gte('match_date', now)
        .order('match_date', { ascending: true })
        .limit(10);

      if (upcomingData) {
        const playerIds = new Set<string>();
        upcomingData.forEach(m => {
          if (m.team_a) playerIds.add(m.team_a);
          if (m.team_b) playerIds.add(m.team_b);
        });

        const { data: players } = await supabase
          .from('players')
          .select('id, name, avatar')
          .in('id', Array.from(playerIds));

        const playerMap = new Map(players?.map(p => [p.id, p]) || []);

        setUpcomingMatches(upcomingData.map(m => ({
          ...m,
          player_a: m.team_a ? playerMap.get(m.team_a) : null,
          player_b: m.team_b ? playerMap.get(m.team_b) : null,
        })));
      }

      // Fetch recent completed matches
      const { data: recentData } = await supabase
        .from('matches')
        .select(`
          id, match_date, status, team_a, team_b, score_a, score_b,
          division:divisions(name),
          tournament:tournaments(name)
        `)
        .eq('status', 'completed')
        .order('match_date', { ascending: false })
        .limit(10);

      if (recentData) {
        const playerIds = new Set<string>();
        recentData.forEach(m => {
          if (m.team_a) playerIds.add(m.team_a);
          if (m.team_b) playerIds.add(m.team_b);
        });

        const { data: players } = await supabase
          .from('players')
          .select('id, name, avatar')
          .in('id', Array.from(playerIds));

        const playerMap = new Map(players?.map(p => [p.id, p]) || []);

        setRecentMatches(recentData.map(m => ({
          ...m,
          player_a: m.team_a ? playerMap.get(m.team_a) : null,
          player_b: m.team_b ? playerMap.get(m.team_b) : null,
        })));
      }

    } catch (error) {
      console.error('Error fetching match data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getMatchDateLabel = (dateStr: string | null) => {
    if (!dateStr) return 'TBA';
    const date = parseISO(dateStr);
    if (isToday(date)) return 'আজ';
    if (isTomorrow(date)) return 'আগামীকাল';
    return format(date, 'dd MMM', { locale: bn });
  };

  const getMatchTimeLabel = (dateStr: string | null) => {
    if (!dateStr) return '';
    return format(parseISO(dateStr), 'hh:mm a');
  };

  const getPeriodLabel = (period: string) => {
    const labels: Record<string, string> = {
      'first_half': '১ম হাফ',
      'half_time': 'হাফ টাইম',
      'second_half': '২য় হাফ',
      'extra_time': 'অতিরিক্ত সময়',
      'penalties': 'পেনাল্টি',
      'full_time': 'ফুল টাইম',
    };
    return labels[period] || period;
  };

  const activeLiveCount = liveMatches?.length || 0;

  return (
    <>
      {/* Live Match Entry Card */}
      <section className="py-6">
        <div className="container">
          <button
            onClick={() => setIsOpen(true)}
            className="w-full group relative overflow-hidden rounded-2xl bg-gradient-to-br from-red-500/20 via-red-900/30 to-rose-900/20 border border-red-500/30 p-6 transition-all duration-500 hover:scale-[1.02] hover:border-red-500/50 hover:shadow-[0_0_60px_rgba(239,68,68,0.3)]"
          >
            {/* Animated background */}
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(239,68,68,0.15),transparent_70%)] animate-pulse" />
            
            {/* Live indicator pulse */}
            {activeLiveCount > 0 && (
              <div className="absolute top-4 right-4 flex items-center gap-2">
                <span 
                  className="w-3 h-3 bg-red-500 rounded-full"
                  style={{ transform: `scale(${pulseScale})`, transition: 'transform 0.3s ease' }}
                />
                <span className="text-red-400 text-sm font-bold animate-pulse">LIVE</span>
              </div>
            )}
            
            {/* Floating icons */}
            <div className="absolute top-12 right-16 text-2xl animate-bounce" style={{ animationDelay: '0.3s' }}>⚽</div>
            <div className="absolute bottom-4 right-8 text-3xl animate-bounce" style={{ animationDelay: '0.6s' }}>📺</div>
            
            <div className="relative z-10 flex items-center gap-4">
              <div className="p-4 rounded-2xl bg-gradient-to-br from-red-500/30 to-rose-600/20 border border-red-500/40 shadow-[0_0_30px_rgba(239,68,68,0.2)]">
                <Radio className="h-10 w-10 text-red-400" />
              </div>
              <div className="flex-1 text-left">
                <h2 className="font-display text-2xl text-red-400 flex items-center gap-2">
                  Live Match Center
                  {activeLiveCount > 0 && (
                    <span className="px-2 py-0.5 text-xs bg-red-500/20 border border-red-500/30 rounded-full text-red-400">
                      {activeLiveCount} লাইভ
                    </span>
                  )}
                </h2>
                <p className="text-muted-foreground text-sm mt-1">
                  লাইভ ম্যাচ • আসন্ন শিডিউল • সাম্প্রতিক রেজাল্ট
                </p>
              </div>
              <ChevronRight className="h-6 w-6 text-red-400 group-hover:translate-x-1 transition-transform" />
            </div>

            {/* Quick stats bar */}
            <div className="relative z-10 mt-4 pt-4 border-t border-red-500/20 grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-red-400">{activeLiveCount}</div>
                <div className="text-xs text-muted-foreground">লাইভ</div>
              </div>
              <div>
                <div className="text-lg font-bold text-amber-400">{upcomingMatches.length || '—'}</div>
                <div className="text-xs text-muted-foreground">আসন্ন</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-400">{recentMatches.length || '—'}</div>
                <div className="text-xs text-muted-foreground">সম্পন্ন</div>
              </div>
            </div>
          </button>
        </div>
      </section>

      {/* Live Match Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0 bg-gradient-to-b from-card via-card to-background border-red-500/30">
          <DialogHeader className="p-6 pb-0 border-b border-border/50 bg-gradient-to-r from-red-500/10 via-transparent to-red-500/10">
            <DialogTitle className="flex items-center gap-3 text-2xl">
              <div className="p-2 rounded-xl bg-red-500/20 border border-red-500/30">
                <Radio className="h-6 w-6 text-red-400" />
              </div>
              <span className="bg-gradient-to-r from-red-400 via-rose-400 to-red-500 bg-clip-text text-transparent font-display">
                Live Match Center
              </span>
              {activeLiveCount > 0 && (
                <span className="ml-2 px-2 py-1 text-xs bg-red-500 text-white rounded-full animate-pulse">
                  {activeLiveCount} LIVE
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="live" className="flex-1">
            <div className="px-6 pt-4">
              <TabsList className="grid w-full grid-cols-3 bg-muted/50">
                <TabsTrigger value="live" className="data-[state=active]:bg-red-500/20 data-[state=active]:text-red-400">
                  <Radio className="h-4 w-4 mr-2" />
                  লাইভ
                  {activeLiveCount > 0 && (
                    <span className="ml-1 w-5 h-5 flex items-center justify-center bg-red-500 text-white text-xs rounded-full">
                      {activeLiveCount}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="upcoming" className="data-[state=active]:bg-amber-500/20 data-[state=active]:text-amber-400">
                  <Calendar className="h-4 w-4 mr-2" />
                  আসন্ন
                </TabsTrigger>
                <TabsTrigger value="completed" className="data-[state=active]:bg-green-500/20 data-[state=active]:text-green-400">
                  <CheckCircle className="h-4 w-4 mr-2" />
                  সম্পন্ন
                </TabsTrigger>
              </TabsList>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {isLoading || liveLoading ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-center">
                    <Radio className="h-12 w-12 text-red-400 mx-auto animate-pulse" />
                    <p className="text-muted-foreground mt-4">লোড হচ্ছে...</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Live Matches Tab */}
                  <TabsContent value="live" className="mt-0 space-y-4">
                    {liveMatches && liveMatches.length > 0 ? (
                      liveMatches.map((match) => (
                        <LiveMatchCard key={match.id} match={match} />
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                          <Radio className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">কোন লাইভ ম্যাচ নেই</h3>
                        <p className="text-muted-foreground text-sm">
                          বর্তমানে কোন ম্যাচ লাইভ নেই। আসন্ন ম্যাচ দেখুন।
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Upcoming Matches Tab */}
                  <TabsContent value="upcoming" className="mt-0 space-y-3">
                    {upcomingMatches.length > 0 ? (
                      upcomingMatches.map((match) => (
                        <ScheduledMatchCard 
                          key={match.id} 
                          match={match} 
                          getDateLabel={getMatchDateLabel}
                          getTimeLabel={getMatchTimeLabel}
                        />
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                          <Calendar className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">কোন আসন্ন ম্যাচ নেই</h3>
                        <p className="text-muted-foreground text-sm">
                          এখনো কোন ম্যাচ শিডিউল করা হয়নি।
                        </p>
                      </div>
                    )}
                  </TabsContent>

                  {/* Completed Matches Tab */}
                  <TabsContent value="completed" className="mt-0 space-y-3">
                    {recentMatches.length > 0 ? (
                      recentMatches.map((match) => (
                        <CompletedMatchCard 
                          key={match.id} 
                          match={match}
                          getDateLabel={getMatchDateLabel}
                        />
                      ))
                    ) : (
                      <div className="text-center py-12">
                        <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-muted/50 flex items-center justify-center">
                          <Trophy className="h-10 w-10 text-muted-foreground" />
                        </div>
                        <h3 className="text-lg font-medium mb-2">কোন সম্পন্ন ম্যাচ নেই</h3>
                        <p className="text-muted-foreground text-sm">
                          এখনো কোন ম্যাচ সম্পন্ন হয়নি।
                        </p>
                      </div>
                    )}
                  </TabsContent>
                </>
              )}
            </div>
          </Tabs>

          {/* Footer */}
          <div className="p-4 border-t border-border/50 bg-muted/30">
            <Link 
              to="/live"
              className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-red-500/20 hover:bg-red-500/30 text-red-400 font-medium transition-all"
              onClick={() => setIsOpen(false)}
            >
              <Play className="h-4 w-4" />
              সকল ম্যাচ দেখুন
              <ChevronRight className="h-4 w-4" />
            </Link>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

// Live Match Card Component
function LiveMatchCard({ match }: { match: any }) {
  const [matchDetails, setMatchDetails] = useState<any>(null);

  useEffect(() => {
    const fetchMatchDetails = async () => {
      if (match.match_type === 'division' || match.match_type === 'tournament') {
        const { data } = await supabase
          .from('matches')
          .select(`
            id, team_a, team_b,
            player_a:players!matches_team_a_fkey(id, name, avatar),
            player_b:players!matches_team_b_fkey(id, name, avatar)
          `)
          .eq('id', match.match_id)
          .single();
        
        if (data) setMatchDetails(data);
      }
    };
    fetchMatchDetails();
  }, [match.match_id, match.match_type]);

  return (
    <Link 
      to={`/live/${match.id}`}
      className="block p-4 rounded-xl bg-gradient-to-r from-red-500/10 to-transparent border border-red-500/30 hover:border-red-500/50 transition-all"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <span className="text-xs text-red-400 font-medium">LIVE</span>
          <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
            {match.current_period === 'first_half' ? '১ম হাফ' : 
             match.current_period === 'second_half' ? '২য় হাফ' : 
             match.current_period === 'half_time' ? 'হাফ টাইম' : match.current_period}
          </span>
        </div>
        <ChevronRight className="h-4 w-4 text-muted-foreground" />
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <img 
            src={matchDetails?.player_a?.avatar || '/placeholder.svg'} 
            alt="" 
            className="w-10 h-10 rounded-full border-2 border-border object-cover"
          />
          <span className="font-medium truncate">{matchDetails?.player_a?.name || 'Home'}</span>
        </div>

        <div className="px-4 py-2 bg-card rounded-lg border border-border mx-4">
          <span className="text-2xl font-bold">{match.score_home} - {match.score_away}</span>
        </div>

        <div className="flex items-center gap-3 flex-1 justify-end">
          <span className="font-medium truncate text-right">{matchDetails?.player_b?.name || 'Away'}</span>
          <img 
            src={matchDetails?.player_b?.avatar || '/placeholder.svg'} 
            alt="" 
            className="w-10 h-10 rounded-full border-2 border-border object-cover"
          />
        </div>
      </div>
    </Link>
  );
}

// Scheduled Match Card Component
function ScheduledMatchCard({ 
  match, 
  getDateLabel, 
  getTimeLabel 
}: { 
  match: ScheduledMatch; 
  getDateLabel: (date: string | null) => string;
  getTimeLabel: (date: string | null) => string;
}) {
  return (
    <div className="p-4 rounded-xl bg-muted/30 border border-border/50 hover:border-amber-500/30 transition-all">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-amber-400" />
          <span className="text-sm font-medium text-amber-400">{getDateLabel(match.match_date)}</span>
          <span className="text-xs text-muted-foreground">{getTimeLabel(match.match_date)}</span>
        </div>
        {(match.division || match.tournament) && (
          <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
            {match.division?.name || match.tournament?.name}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-1">
          <img 
            src={match.player_a?.avatar || '/placeholder.svg'} 
            alt="" 
            className="w-10 h-10 rounded-full border-2 border-border object-cover"
          />
          <span className="font-medium truncate">{match.player_a?.name || 'TBA'}</span>
        </div>

        <div className="px-4 py-2 text-muted-foreground mx-4">
          <span className="text-lg font-medium">VS</span>
        </div>

        <div className="flex items-center gap-3 flex-1 justify-end">
          <span className="font-medium truncate text-right">{match.player_b?.name || 'TBA'}</span>
          <img 
            src={match.player_b?.avatar || '/placeholder.svg'} 
            alt="" 
            className="w-10 h-10 rounded-full border-2 border-border object-cover"
          />
        </div>
      </div>
    </div>
  );
}

// Completed Match Card Component
function CompletedMatchCard({ 
  match,
  getDateLabel
}: { 
  match: ScheduledMatch;
  getDateLabel: (date: string | null) => string;
}) {
  const isHomeWin = (match.score_a || 0) > (match.score_b || 0);
  const isAwayWin = (match.score_b || 0) > (match.score_a || 0);
  const isDraw = match.score_a === match.score_b;

  return (
    <div className="p-4 rounded-xl bg-muted/30 border border-border/50 hover:border-green-500/30 transition-all">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <CheckCircle className="h-4 w-4 text-green-400" />
          <span className="text-sm text-muted-foreground">{getDateLabel(match.match_date)}</span>
        </div>
        {(match.division || match.tournament) && (
          <span className="text-xs px-2 py-0.5 bg-muted rounded text-muted-foreground">
            {match.division?.name || match.tournament?.name}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-3 flex-1 ${isHomeWin ? 'opacity-100' : 'opacity-60'}`}>
          <img 
            src={match.player_a?.avatar || '/placeholder.svg'} 
            alt="" 
            className={`w-10 h-10 rounded-full border-2 object-cover ${isHomeWin ? 'border-green-500' : 'border-border'}`}
          />
          <div>
            <span className="font-medium truncate block">{match.player_a?.name || 'Player A'}</span>
            {isHomeWin && <span className="text-xs text-green-400">🏆 বিজয়ী</span>}
          </div>
        </div>

        <div className="px-4 py-2 bg-card rounded-lg border border-border mx-4">
          <span className="text-xl font-bold">
            <span className={isHomeWin ? 'text-green-400' : ''}>{match.score_a ?? 0}</span>
            {' - '}
            <span className={isAwayWin ? 'text-green-400' : ''}>{match.score_b ?? 0}</span>
          </span>
        </div>

        <div className={`flex items-center gap-3 flex-1 justify-end ${isAwayWin ? 'opacity-100' : 'opacity-60'}`}>
          <div className="text-right">
            <span className="font-medium truncate block">{match.player_b?.name || 'Player B'}</span>
            {isAwayWin && <span className="text-xs text-green-400">🏆 বিজয়ী</span>}
          </div>
          <img 
            src={match.player_b?.avatar || '/placeholder.svg'} 
            alt="" 
            className={`w-10 h-10 rounded-full border-2 object-cover ${isAwayWin ? 'border-green-500' : 'border-border'}`}
          />
        </div>
      </div>
    </div>
  );
}
